import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  TEACHER_DEFAULT_PASSCODE,
  THEME_OPTIONS,
  TIME_OPTIONS,
  blankLesson,
  deleteCustomLesson,
  ensureBuiltInsSeeded,
  getCustomLesson,
  isTeacherUnlocked,
  listCustomLessons,
  newWord,
  saveCustomLesson,
  setTeacherUnlocked,
  type CustomLesson,
} from "../local/customLessons";
import { WORD_PACKS } from "../data/word-packs";
import { findImageUrlForWord } from "../local/emojiCatalog";
import {
  downloadCsv,
  importCsv,
  lessonsToCsv,
  type CsvImportReport,
} from "../local/csvLessons";
import type { ThemeId, TimeOfDay, WordDTO } from "@shared/types";

/**
 * Spreadsheet-style teacher dashboard. One tab per lesson at the top
 * (Excel / Google Sheets feel), a real table below with editable cells,
 * and a toolbar that handles import/export of a single CSV file
 * containing every lesson.
 *
 *  /teacher           → spreadsheet workspace (passcode gated)
 *  /teacher/:lessonId → legacy single-lesson editor (kept so existing
 *                       links continue to work)
 */

// ──────────────────────────────────────────────────────────────────────
// Passcode gate
// ──────────────────────────────────────────────────────────────────────
export function LocalTeacher() {
  const [unlocked, setUnlocked] = useState(() => isTeacherUnlocked());
  const [passcode, setPasscode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const nav = useNavigate();

  if (unlocked) return <TeacherWorkspace />;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (passcode.trim() === TEACHER_DEFAULT_PASSCODE) {
      setTeacherUnlocked(true);
      setUnlocked(true);
    } else {
      setError("Wrong passcode.");
    }
  };
  return (
    <div className="teacher-sheet" style={{ alignItems: "center", justifyContent: "center" }}>
      <div className="stack" style={{ width: "min(420px, 90%)", padding: 24 }}>
        <button className="btn btn-ghost" onClick={() => nav("/")} style={{ alignSelf: "flex-start" }}>← Menu</button>
        <h1 style={{ textAlign: "center", color: "#1f2937", margin: 0 }}>Teacher mode</h1>
        <p style={{ textAlign: "center", color: "#4b5563", margin: 0 }}>Enter the teacher passcode.</p>
        <form onSubmit={submit} className="card stack" style={{ background: "#ffffff", padding: 20, border: "1px solid #d1d5db", borderRadius: 12 }}>
          <input
            className="input"
            type="password"
            placeholder="Passcode"
            value={passcode}
            onChange={(e) => { setPasscode(e.target.value); setError(null); }}
            autoFocus
            style={{ textAlign: "center", fontSize: "1.2rem", color: "#1f2937", background: "#ffffff" }}
          />
          {error && <span style={{ color: "#dc2626", textAlign: "center" }}>{error}</span>}
          <button type="submit" className="btn btn-primary btn-big btn-block" style={{ color: "#ffffff" }}>Unlock</button>
          <p style={{ textAlign: "center", color: "#6b7280", margin: 0, fontSize: "0.85rem" }}>
            Default passcode: <code style={{ background: "#e5e7eb", padding: "2px 6px", borderRadius: 4 }}>{TEACHER_DEFAULT_PASSCODE}</code>
          </p>
        </form>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Spreadsheet workspace
// ──────────────────────────────────────────────────────────────────────
function TeacherWorkspace() {
  const nav = useNavigate();
  // First-visit seed: clone every built-in WORD_PACK into the workbook
  // so the teacher can SEE the structure and edit ready-made packs as a
  // starting point. Idempotent — only runs on a truly empty workbook.
  if (typeof window !== "undefined") ensureBuiltInsSeeded(WORD_PACKS);
  const [lessons, setLessons] = useState<CustomLesson[]>(() => listCustomLessons());
  const [activeId, setActiveId] = useState<string | null>(() => listCustomLessons()[0]?.id ?? null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [importReport, setImportReport] = useState<CsvImportReport | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Whenever the underlying lesson list changes externally (e.g. CSV
  // import) make sure activeId still points at a real lesson.
  useEffect(() => {
    if (lessons.length === 0) { setActiveId(null); return; }
    if (!activeId || !lessons.find((l) => l.id === activeId)) {
      setActiveId(lessons[0]!.id);
    }
  }, [lessons, activeId]);

  const active = useMemo(
    () => lessons.find((l) => l.id === activeId) ?? null,
    [lessons, activeId],
  );

  const refresh = () => setLessons(listCustomLessons());

  // ── Tab actions ───────────────────────────────────────────────────
  const addTab = () => {
    const l = saveCustomLesson(blankLesson());
    refresh();
    setActiveId(l.id);
  };
  const removeTab = (id: string) => {
    if (!confirm("Delete this lesson? This can't be undone.")) return;
    deleteCustomLesson(id);
    refresh();
  };
  const renameTab = (id: string, nextTitle: string) => {
    const l = lessons.find((x) => x.id === id);
    if (!l) return;
    const saved = saveCustomLesson({ ...l, title: nextTitle.trim() || "Untitled" });
    setLessons((prev) => prev.map((x) => (x.id === id ? saved : x)));
    setSavedAt(Date.now());
  };

  // ── Cell edits — every change persists immediately (Sheets feel). ──
  const updateLesson = (patch: Partial<CustomLesson>) => {
    if (!active) return;
    const next = saveCustomLesson({ ...active, ...patch });
    setLessons((prev) => prev.map((l) => (l.id === active.id ? next : l)));
    setSavedAt(Date.now());
  };
  const updateWord = (idx: number, patch: Partial<WordDTO>) => {
    if (!active) return;
    const words = active.words.slice();
    const cur = { ...words[idx]!, ...patch };
    // Auto-resolve image when english changes.
    if (patch.english !== undefined) {
      const suggested = findImageUrlForWord(cur.english);
      if (suggested && (!cur.imageUrl || cur.imageUrl.startsWith("/images/openmoji/"))) {
        cur.imageUrl = suggested;
      }
    }
    words[idx] = cur;
    updateLesson({ words });
  };
  const addRow = () => {
    if (!active) return;
    const w = newWord();
    w.orderIndex = active.words.length;
    updateLesson({ words: [...active.words, w] });
  };
  const removeRow = (idx: number) => {
    if (!active) return;
    updateLesson({ words: active.words.filter((_, i) => i !== idx) });
  };

  // ── CSV import / export ──────────────────────────────────────────
  const exportCsv = () => {
    const csv = lessonsToCsv(lessons);
    const date = new Date().toISOString().slice(0, 10);
    downloadCsv(csv, `word-runner-lessons-${date}.csv`);
  };
  const onImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-importing the same file
    if (!file) return;
    const text = await file.text();
    const report = importCsv(text);
    setImportReport(report);
    refresh();
  };

  const signOut = () => {
    setTeacherUnlocked(false);
    nav("/", { replace: true });
  };

  return (
    <div className="teacher-sheet">
      <header className="teacher-sheet-top">
        <button className="btn btn-ghost" onClick={() => nav("/")}>← Menu</button>
        <h1 className="teacher-sheet-title">Word Pack Workbook</h1>
        <div className="teacher-sheet-toolbar">
          {savedAt && (
            <span className="teacher-sheet-saved">
              Saved {new Date(savedAt).toLocaleTimeString()}
            </span>
          )}
          <button className="btn btn-ghost" onClick={exportCsv} title="Download every pack as one CSV file">
            ⬇ Export CSV
          </button>
          <button className="btn btn-ghost" onClick={() => fileInputRef.current?.click()} title="Bulk import packs from a CSV file">
            ⬆ Import CSV
          </button>
          <input ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={onImportFile} style={{ display: "none" }} />
          <Link to="/printables" className="btn btn-ghost" title="Printable flashcards — one page per word pack">
            🖨 Printable Cards
          </Link>
          {active && (
            <Link to={`/play/lesson/${active.id}`} className="btn btn-green">▶ Play this pack</Link>
          )}
          <button className="btn btn-ghost" onClick={signOut}>Sign out</button>
        </div>
      </header>

      {importReport && (
        <div className="teacher-sheet-banner">
          ✓ Imported <strong>{importReport.lessonsParsed}</strong> pack{importReport.lessonsParsed === 1 ? "" : "s"} ({importReport.lessonsCreated} new, {importReport.lessonsUpdated} updated) with <strong>{importReport.wordsImported}</strong> words.
          {importReport.warnings.length > 0 && (
            <ul style={{ margin: "6px 0 0 16px", padding: 0 }}>
              {importReport.warnings.slice(0, 5).map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          )}
          <button className="btn btn-ghost" onClick={() => setImportReport(null)} style={{ marginLeft: 8 }}>×</button>
        </div>
      )}

      <nav className="teacher-tabs" role="tablist">
        {lessons.map((l) => (
          <TabButton
            key={l.id}
            label={l.title}
            badge={`${l.words.length}`}
            active={l.id === activeId}
            onSelect={() => setActiveId(l.id)}
            onRename={(t) => renameTab(l.id, t)}
            onDelete={() => removeTab(l.id)}
          />
        ))}
        <button className="teacher-tab teacher-tab-add" onClick={addTab} title="New word pack">+ New Pack</button>
      </nav>

      {active ? (
        <SpreadsheetBody
          lesson={active}
          onUpdate={updateLesson}
          onUpdateWord={updateWord}
          onAddRow={addRow}
          onRemoveRow={removeRow}
        />
      ) : (
        <div className="teacher-sheet-empty">
          <p>No word packs yet — click <strong>+ New Pack</strong> or <strong>Import CSV</strong> to get started.</p>
          <p className="t-caption t-muted" style={{ textTransform: "none" }}>
            The CSV format is one row per word with columns: <code>lesson_id, lesson_title, theme, time_of_day, word_id, english, vietnamese, sentence, image_url, order</code>.
            Editable in Excel, Google Sheets, Numbers — anything that opens .csv.
          </p>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Renameable tab — double-click to edit, Enter / blur to save.
// ──────────────────────────────────────────────────────────────────────
function TabButton({
  label, badge, active, onSelect, onRename, onDelete,
}: {
  label: string;
  badge?: string;
  active: boolean;
  onSelect: () => void;
  onRename: (next: string) => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(label);
  useEffect(() => { setDraft(label); }, [label]);

  if (editing) {
    return (
      <span className={`teacher-tab ${active ? "is-active" : ""}`}>
        <input
          className="teacher-tab-edit"
          value={draft}
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => { setEditing(false); if (draft.trim()) onRename(draft); else setDraft(label); }}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.currentTarget as HTMLInputElement).blur();
            if (e.key === "Escape") { setDraft(label); setEditing(false); }
          }}
        />
      </span>
    );
  }
  return (
    <span className={`teacher-tab ${active ? "is-active" : ""}`}>
      <button className="teacher-tab-label" onClick={onSelect} onDoubleClick={() => setEditing(true)} title="Click to switch · Double-click to rename">
        {label}
        {badge && <span className="teacher-tab-badge">{badge}</span>}
      </button>
      <button className="teacher-tab-close" onClick={onDelete} title="Delete pack">×</button>
    </span>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Spreadsheet body — lesson meta + word table
// ──────────────────────────────────────────────────────────────────────
function SpreadsheetBody({
  lesson, onUpdate, onUpdateWord, onAddRow, onRemoveRow,
}: {
  lesson: CustomLesson;
  onUpdate: (patch: Partial<CustomLesson>) => void;
  onUpdateWord: (idx: number, patch: Partial<WordDTO>) => void;
  onAddRow: () => void;
  onRemoveRow: (idx: number) => void;
}) {
  return (
    <div className="teacher-sheet-body">
      <div className="teacher-meta-bar">
        <label className="teacher-meta-field">
          <span>Pack title</span>
          <input
            className="input"
            value={lesson.title}
            onChange={(e) => onUpdate({ title: e.target.value })}
          />
        </label>
        <label className="teacher-meta-field">
          <span>Theme</span>
          <select className="input" value={lesson.theme} onChange={(e) => onUpdate({ theme: e.target.value as ThemeId })}>
            {THEME_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label className="teacher-meta-field">
          <span>Time of day</span>
          <select className="input" value={lesson.timeOfDay} onChange={(e) => onUpdate({ timeOfDay: e.target.value as TimeOfDay })}>
            {TIME_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <span className="teacher-meta-info">
          Pack ID <code>{lesson.id}</code>
        </span>
      </div>

      <div className="teacher-grid">
        <table>
          <thead>
            <tr>
              <th className="teacher-col-row">#</th>
              <th className="teacher-col-img">Image</th>
              <th>English</th>
              <th>Vietnamese</th>
              <th>Example sentence</th>
              <th className="teacher-col-image-url">Image URL (optional)</th>
              <th className="teacher-col-actions" />
            </tr>
          </thead>
          <tbody>
            {lesson.words.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: "center", padding: 24 }}>
                No words yet. Click <strong>+ Add row</strong> below.
              </td></tr>
            ) : (
              lesson.words.map((w, i) => (
                <WordRow key={w.id} word={w} index={i} onChange={(p) => onUpdateWord(i, p)} onRemove={() => onRemoveRow(i)} />
              ))
            )}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={7} className="teacher-grid-add">
                <button className="btn btn-green" onClick={onAddRow}>+ Add row</button>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function WordRow({
  word, index, onChange, onRemove,
}: {
  word: WordDTO;
  index: number;
  onChange: (patch: Partial<WordDTO>) => void;
  onRemove: () => void;
}) {
  const userEditedVi = useRef(false);
  const [translating, setTranslating] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  useEffect(() => { setPreviewError(false); }, [word.imageUrl]);

  const translate = async (englishOverride?: string) => {
    const src = (englishOverride ?? word.english).trim();
    if (!src) return;
    setTranslating(true);
    try {
      const url = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(src)}&langpair=en|vi`;
      const r = await fetch(url);
      const j = (await r.json()) as { responseData?: { translatedText?: string } };
      const out = j?.responseData?.translatedText?.trim();
      if (out) {
        onChange({ vietnamese: out });
        userEditedVi.current = false;
      }
    } catch { /* network — silent */ }
    finally { setTranslating(false); }
  };
  // Auto-translate when english changes AND user hasn't typed vietnamese yet.
  useEffect(() => {
    const eng = word.english.trim();
    if (!eng || userEditedVi.current) return;
    const t = window.setTimeout(() => translate(eng), 700);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [word.english]);

  const hasImage = !!word.imageUrl && !previewError;
  return (
    <tr>
      <td className="teacher-col-row">{index + 1}</td>
      <td className="teacher-col-img">
        {hasImage ? (
          <img src={word.imageUrl!} alt={word.english} onError={() => setPreviewError(true)} />
        ) : (
          <span className="teacher-thumb-empty">?</span>
        )}
      </td>
      <td>
        <input className="teacher-cell" value={word.english} placeholder="English"
          onChange={(e) => onChange({ english: e.target.value })} />
      </td>
      <td>
        <div className="teacher-vi-cell">
          <input className="teacher-cell" value={word.vietnamese} placeholder="Vietnamese"
            onChange={(e) => { userEditedVi.current = true; onChange({ vietnamese: e.target.value }); }} />
          <button className="teacher-translate" onClick={() => translate()} disabled={translating || !word.english.trim()} title="Auto-translate">
            {translating ? "…" : "→VI"}
          </button>
        </div>
      </td>
      <td>
        <input className="teacher-cell" value={word.sentence} placeholder="Example sentence"
          onChange={(e) => onChange({ sentence: e.target.value })} />
      </td>
      <td className="teacher-col-image-url">
        <input className="teacher-cell" value={word.imageUrl ?? ""} placeholder="(auto)"
          onChange={(e) => onChange({ imageUrl: e.target.value || null })} />
      </td>
      <td className="teacher-col-actions">
        <button className="btn btn-ghost teacher-row-remove" onClick={onRemove} title="Delete row">×</button>
      </td>
    </tr>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Legacy single-lesson editor — preserved so /teacher/:id links work.
// ──────────────────────────────────────────────────────────────────────
export function LocalTeacherLessonEditor() {
  const { lessonId } = useParams<{ lessonId: string }>();
  const nav = useNavigate();
  const [lesson, setLesson] = useState<CustomLesson | null>(null);

  useEffect(() => {
    if (!lessonId) return;
    setLesson(getCustomLesson(lessonId));
  }, [lessonId]);

  if (!isTeacherUnlocked()) { nav("/teacher", { replace: true }); return null; }
  if (!lesson) return <div className="full-center">Loading…</div>;

  return (
    <div className="shell-narrow stack" style={{ paddingTop: "4vh", paddingBottom: 48 }}>
      <button className="btn btn-ghost" onClick={() => nav("/teacher")}>← Workbook</button>
      <h1 className="t-title">{lesson.title}</h1>
      <p className="t-body t-muted">This pack opens in the workbook. Use the workbook for editing.</p>
      <Link to={`/play/lesson/${lesson.id}`} className="btn btn-green btn-big btn-block">Play this pack</Link>
    </div>
  );
}
